import React, { useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Task } from '../types';
import {
  RecurrencePattern, RecurrenceInput,
  getTaskRecurrencePattern, createRecurrence, deleteRecurrencePattern,
  describePattern,
} from '../services/recurrenceService';
import RecurrenceControls, { DEFAULT_RECURRENCE } from '../components/RecurrenceControls';

// Recurrence section of the task detail drawer. Three states:
//   • the task IS a generated instance → read-only note linking identity
//   • the task owns a pattern → summary + remove (instances are kept)
//   • no pattern → "Make Recurring" reveals the shared pattern editor
export default function RecurrencePane({ pca, task }: { pca: IPublicClientApplication; task: Task }) {
  const [pattern, setPattern] = useState<RecurrencePattern | null | undefined>(undefined);
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState<RecurrenceInput>(DEFAULT_RECURRENCE);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPattern(undefined); setEditing(false); setConfirmRemove(false); setMessage(null);
    if (task.dagrun_pm_isrecurringinstance) { setPattern(null); return; }
    getTaskRecurrencePattern(pca, task.dagrun_pm_taskid)
      .then(p => { if (!cancelled) setPattern(p); })
      .catch(() => { if (!cancelled) setPattern(null); });
    return () => { cancelled = true; };
  }, [pca, task.dagrun_pm_taskid, task.dagrun_pm_isrecurringinstance]);

  const save = async () => {
    if (saving) return;
    if (input.end.type === 'date' && !input.end.date) { setMessage('Pick an end date first.'); return; }
    setSaving(true); setMessage(null);
    try {
      const { patternId, instancesCreated } = await createRecurrence(pca, task, input);
      setPattern({
        dagrun_pm_recurrencepatternid: patternId,
        dagrun_pm_name: '',
        dagrun_pm_patterntype: 0,
        dagrun_pm_endtype: 0,
      } as RecurrencePattern);
      // Refetch for an accurate summary line.
      getTaskRecurrencePattern(pca, task.dagrun_pm_taskid).then(p => { if (p) setPattern(p); }).catch(() => {});
      setEditing(false);
      setMessage(`Recurring series created — ${instancesCreated} instance${instancesCreated === 1 ? '' : 's'} generated.`);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Failed to create recurrence');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!pattern || saving) return;
    setSaving(true); setMessage(null);
    try {
      await deleteRecurrencePattern(pca, pattern.dagrun_pm_recurrencepatternid);
      setPattern(null); setConfirmRemove(false);
      setMessage('Recurrence removed. Already-generated instances were kept.');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Failed to remove recurrence');
    } finally { setSaving(false); }
  };

  if (task.dagrun_pm_isrecurringinstance) {
    return (
      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
        This task is a generated instance of a recurring series
        {task.dagrun_pm_occurrencenumber != null ? ` (occurrence #${task.dagrun_pm_occurrencenumber})` : ''}.
        Manage the pattern from the series template task.
      </div>
    );
  }

  if (pattern === undefined) {
    return <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading…</div>;
  }

  return (
    <div>
      {pattern ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 10px', borderRadius: 10,
            background: 'var(--brand-mist)', color: 'var(--brand)',
            fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-ui)',
          }}>↻ {describePattern(pattern)}</span>
          <div style={{ flex: 1 }} />
          {confirmRemove ? (
            <>
              <button type="button" onClick={remove} disabled={saving}
                style={{ background: '#DC2626', color: '#fff', border: 'none', padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, cursor: 'pointer' }}
              >{saving ? 'Removing…' : 'Confirm remove'}</button>
              <button type="button" onClick={() => setConfirmRemove(false)} disabled={saving}
                style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--ink-3)', padding: '4px 10px', fontSize: 11, borderRadius: 6, cursor: 'pointer' }}
              >Cancel</button>
            </>
          ) : (
            <button type="button" onClick={() => setConfirmRemove(true)}
              style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--ink-3)', padding: '4px 10px', fontSize: 11, borderRadius: 6, cursor: 'pointer' }}
            >Remove recurrence</button>
          )}
        </div>
      ) : editing ? (
        <div>
          <RecurrenceControls value={input} onChange={setInput} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" onClick={save} disabled={saving}
              style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
            >{saving ? 'Generating…' : 'Save & Generate Instances'}</button>
            <button type="button" onClick={() => setEditing(false)} disabled={saving}
              style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--ink-3)', padding: '6px 14px', fontSize: 12, borderRadius: 6, cursor: 'pointer' }}
            >Cancel</button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 6 }}>
            Instances are generated from this task's due date (or start date).
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setEditing(true)}
          style={{ background: 'transparent', border: '1px solid #0E7490', color: '#0E7490', padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer' }}
        >↻ Make Recurring</button>
      )}
      {message && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-2)' }}>{message}</div>}
    </div>
  );
}
